import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, User, Heart, Clock } from 'lucide-react';
import { toast } from 'sonner';

const formatTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const ConversationDetailPage = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMessages = useCallback(async () => {
    try {
      const response = await api.get(`/conversation/${conversationId}/messages`);
      setMessages(response.data);
    } catch {
      toast.error('Failed to load conversation');
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] py-12 px-4 flex items-center justify-center">
        <p className="text-[#A1A1AA]">Loading conversation...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12 px-4" data-testid="conversation-detail-page">
      <div className="max-w-3xl mx-auto">
        <Button onClick={() => navigate('/conversations')} variant="ghost" className="mb-6 text-[#A1A1AA] hover:text-[#F9FAFB]" data-testid="back-to-conversations">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to History
        </Button>

        {messages.length === 0 ? (
          <Card className="card-surface border-[#27272A]">
            <CardContent className="py-12 text-center">
              <p className="text-[#A1A1AA]">No messages in this conversation</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {messages.map((msg) => (
              <div key={msg.message_id} className="space-y-3">
                <Card className="card-surface border-[#27272A] border-l-4 border-l-[#8B5CF6]">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                      <User className="w-4 h-4" />
                      <span>You</span>
                      <Clock className="w-3 h-3 ml-auto" />
                      <span className="text-xs">{formatTime(msg.created_at)}</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[#F9FAFB] leading-relaxed">{msg.user_message}</p>
                  </CardContent>
                </Card>

                <Card className="card-surface border-[#27272A] border-l-4 border-l-[#10B981] ml-4">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                      <Heart className="w-4 h-4 text-[#10B981]" />
                      <span>I May Help You</span>
                      <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-[#8B5CF6]/20 text-[#8B5CF6]">{msg.response_style}</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[#D4D4D8] leading-relaxed">{msg.ai_response}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationDetailPage;
